import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import React from "react";
import { useUrlSearchParams } from "../commons/hooks/useUrlSearchParams.js";
import { REF_TOKEN_URL_SEARCH_PARAM } from "../constants.js";
import { InvitationTokenContext } from "./InvitationTokenContext.js";
import { InvitationTokenData } from "./InvitationTokenData.js";
import { useRefTokenQuery } from "./InvitationTokenProvider.generated.js";

export const InvitationTokenProvider = ({
  children,
}: React.PropsWithChildren<{}>) => {
  const { token: urlToken, remove } = useUrlToken();
  const { token, tokenData, isLoading } = useInvitationToken(urlToken);

  return (
    <InvitationTokenContext.Provider
      value={{ token, tokenData, isLoading, remove }}
    >
      {children}
    </InvitationTokenContext.Provider>
  );
};

function useUrlToken(name: string = REF_TOKEN_URL_SEARCH_PARAM) {
  const { urlSearchParams, setUrlSearchParams } = useUrlSearchParams();
  const token = urlSearchParams.get(name);

  const remove = React.useCallback(() => {
    setUrlSearchParams({}, [name]);
  }, [name, setUrlSearchParams]);

  return { token, remove };
}

function useInvitationToken(refToken: string | null) {
  const client = useApolloClient();
  const query = useRefTokenQuery({
    client,
    variables: {
      // `skip` below keeps the query from running while this is null, so the
      // placeholder is never sent — a `!` assertion would claim a narrowing
      // the type does not carry.
      refToken: refToken ?? "",
    },
    skip: !refToken,
  });

  // `organisationMembershipInvitation` sits behind an extra wrapper object
  // (`OrganisationMembershipInvitationQueryResult`) whose non-null `result`
  // field is itself the union of the pending invitation and the data-free
  // `InvitationNoLongerOpen` member — unwrap both levels here so a spent
  // invitation still ends up as a truthy `InvitationNoLongerOpen` object and
  // does not fall through the `??` chain to the next invitation type.
  const organisationMembershipInvitationResult = getDataOrNull(
    query.data?.organisationMembershipInvitation
  )?.result;

  const tokenData: InvitationTokenData | null = refToken
    ? (organisationMembershipInvitationResult ??
      query.data?.projectContracteeInvitation ??
      query.data?.crmLinkingInvitation ??
      query.data?.referralInfo ??
      null)
    : null;

  const token = tokenData ? refToken : null;

  return {
    token,
    tokenData,
    isLoading: query.loading,
  };
}
